import { Injectable } from '@angular/core';
import * as localforage from 'localforage';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  getEntity,
  selectAllEntities,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IDeleteParams,
  IListParams,
  IPaginationState,
  IScrollPosition,
  IStoreProps,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IOrdering } from './ordering.interface';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl2-ordering';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IOrdering, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class OrderingCoreService {
  private _loading = false;

  /**
   * @name orderingList$
   * @description emits stored list of the orders
   */

  orderingList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      // avoids call of getOrderings on each page refresh
      setTimeout(() => {
        this.refresh();
      }, 300);
    } else {
      this.refresh();
    }
  }

  public getOrderings(params?: IListParams): void {
    this._loading = true;
    this._api
      .get(`orderings/list`, params || {})
      .subscribe((data: IOrderingListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public addOrdering(dataInput): Observable<any> {
    this._loading = true;
    return this._api.post('orderings/add', dataInput || {}).pipe(
      tap((data: IOrdering) => {
        store.update(addEntities(data));
        this._loading = false;
      }),
    );
  }

  public updateOrdering(dataInput): Observable<any> {
    this._loading = true;
    return this._api.put('orderings/edit', dataInput || {}).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IOrdering) => {
        store.update(updateEntities(data['_id'], data));
        this._loading = false;
      }),
    );
  }

  public getOrderingById(orderingId: string): Observable<IOrdering> {
    return this._api.get(`orderings/${orderingId}`);
  }

  public deleteOrdering(data: IDeleteParams): Observable<any> {
    return this._api.delete('orderings', { body: data }).pipe(
      tap(() => {
        store.update(deleteEntities(data['_id']));
      }),
    );
  }

  public getOrderingInfo(id: string): IOrdering {
    const ordering = store.query(getEntity(id));
    return ordering;
  }

  public resolveOrderingId(ordering: string | IOrdering): string {
    let orderingId: string | IOrdering = '';
    if (ordering) {
      orderingId = ordering;
    }
    if (ordering['_id']) {
      orderingId = ordering['_id'];
    }
    return orderingId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getOrderings();
    }
  }
}

interface IOrderingListResponse {
  list: IOrdering[];
  totalCount: number;
}
